import { useState, type FormEvent, type MouseEvent } from 'react';
import { formatteerEuro, parseEuroNaarCenten } from '@qc/core';
import type { Evenement, Organisator } from '@qc/db';
import { Knop } from '@qc/ui';
import { Bedrijfsgegevens } from '../beheer/Bedrijfsgegevens.js';
import { Demomodus } from '../beheer/Demomodus.js';
import { Terugbetalingen } from '../beheer/Terugbetalingen.js';
import { Veld } from '../auth/Veld.js';
import { useEvenementen } from './useEvenementen.js';

export interface EvenementenSchermProps {
  /** Leeg bij de beheerder: die ziet alle evenementen maar maakt er geen aan. */
  organisator: Organisator | null;
  onOpen: (evenement: Evenement) => void;
  onUitloggen: () => Promise<void>;
}

const STATUSTEKST: Record<string, string> = {
  concept: 'concept',
  gepland: 'gepland',
  actief: 'bezig',
  afgesloten: 'afgesloten',
  afgerekend: 'afgerekend',
};

function toonDatum(datum: string): string {
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${datum}T12:00:00Z`));
}

/** Het hele veld opent de kalender, niet alleen het kleine icoontje rechts. */
function openKalender(gebeurtenis: MouseEvent<HTMLInputElement>): void {
  try {
    gebeurtenis.currentTarget.showPicker();
  } catch {
    // Browser zonder showPicker: het icoontje in het veld werkt dan nog gewoon.
  }
}

export function EvenementenScherm({ organisator, onOpen, onUitloggen }: EvenementenSchermProps) {
  const evenementen = useEvenementen(organisator?.id ?? null);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [locatie, setLocatie] = useState('');
  const vandaag = new Date().toISOString().slice(0, 10);
  const [startOp, setStartOp] = useState(vandaag);
  const [eindOp, setEindOp] = useState(vandaag);
  const [borg, setBorg] = useState('1,00');
  const [fout, setFout] = useState<string | null>(null);

  async function maakAan(gebeurtenis: FormEvent) {
    gebeurtenis.preventDefault();
    setFout(null);

    let bekerborgCent = 0;
    try {
      bekerborgCent = borg.trim() === '' ? 0 : parseEuroNaarCenten(borg);
    } catch {
      setFout('Vul het statiegeld in als bedrag, bijvoorbeeld 1,00. Geen statiegeld? Vul 0 in.');
      return;
    }
    if (eindOp < startOp) {
      setFout('De einddatum ligt vóór de startdatum.');
      return;
    }

    const gemaakt = await evenementen.maakAan({ naam, locatie, startOp, eindOp, bekerborgCent });
    if (gemaakt !== null) {
      setNieuwOpen(false);
      setNaam('');
      setLocatie('');
      onOpen(gemaakt);
    }
  }

  return (
    <div className="mx-auto min-h-full max-w-4xl p-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="text-tekst-zacht">{organisator?.bedrijfsnaam ?? 'Beheer'}</p>
          <h1 className="text-3xl font-bold">Evenementen</h1>
        </div>
        <Knop nadruk="stil" onClick={() => void onUitloggen()} className="text-base">
          Uitloggen
        </Knop>
      </header>

      {evenementen.fout !== null && <p className="mb-4 text-lg text-fout">{evenementen.fout}</p>}

      {organisator === null && (
        <div className="mb-8 grid gap-6">
          <Bedrijfsgegevens />
          <Terugbetalingen />
          <Demomodus onKlaar={evenementen.ververs} />
        </div>
      )}

      {!nieuwOpen && organisator !== null && (
        <Knop nadruk="hoofd" onClick={() => setNieuwOpen(true)} className="mb-6 w-full">
          Nieuw evenement
        </Knop>
      )}

      {nieuwOpen && organisator !== null && (
        <form
          onSubmit={(e) => void maakAan(e)}
          className="mb-8 grid gap-4 rounded-tegel border border-rand bg-vlak p-6 sm:grid-cols-2"
        >
          <div className="sm:col-span-2">
            <Veld label="Naam" required value={naam} onChange={(e) => setNaam(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Veld label="Locatie" value={locatie} onChange={(e) => setLocatie(e.target.value)} />
          </div>
          <Veld
            label="Eerste dag"
            type="date"
            required
            value={startOp}
            onChange={(e) => setStartOp(e.target.value)}
            onClick={openKalender}
          />
          <Veld
            label="Laatste dag"
            type="date"
            required
            value={eindOp}
            onChange={(e) => setEindOp(e.target.value)}
            onClick={openKalender}
          />
          <div className="sm:col-span-2">
            <Veld
              label="Statiegeld per beker"
              value={borg}
              onChange={(e) => setBorg(e.target.value)}
              toelichting="Vul 0 in als je geen statiegeld rekent."
            />
          </div>

          {fout !== null && <p className="text-lg text-fout sm:col-span-2">{fout}</p>}

          <div className="flex gap-3 sm:col-span-2">
            <Knop nadruk="hoofd" type="submit" className="flex-1">
              Aanmaken
            </Knop>
            <Knop nadruk="gewoon" onClick={() => setNieuwOpen(false)}>
              Annuleren
            </Knop>
          </div>
        </form>
      )}

      {evenementen.laden && <p className="text-tekst-zacht">Bezig met laden…</p>}

      {!evenementen.laden && evenementen.lijst.length === 0 && (
        <p className="text-tekst-zacht">
          {organisator === null
            ? 'Er zijn nog geen evenementen.'
            : 'Je hebt nog geen evenementen. Maak er een aan en nodig je kramen uit.'}
        </p>
      )}

      <ul className="grid gap-3">
        {evenementen.lijst.map((evenement) => (
          <li key={evenement.id}>
            <button
              type="button"
              onClick={() => onOpen(evenement)}
              className="flex w-full items-center justify-between gap-4 rounded-tegel border border-rand bg-vlak p-5 text-left active:bg-vlak-hoog"
            >
              <span className="min-w-0">
                <span className="block truncate text-xl font-semibold">{evenement.naam}</span>
                <span className="block text-tekst-zacht">
                  {toonDatum(evenement.start_op)}
                  {evenement.eind_op !== evenement.start_op
                    ? ` tot en met ${toonDatum(evenement.eind_op)}`
                    : ''}
                  {evenement.bekerborg_cent > 0
                    ? ` · statiegeld ${formatteerEuro(evenement.bekerborg_cent)}`
                    : ''}
                </span>
              </span>
              <span className="shrink-0 rounded-tegel bg-vlak-hoog px-3 py-1 text-base text-tekst-zacht">
                {STATUSTEKST[evenement.status] ?? evenement.status}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
